import { IReplayOptions, TCommandResult } from '@/types';
import { Logger, FileUtils, ValidationUtils } from '@/utils';

/**
 * Replay 명령어 - 기록된 상태 재생
 */
export async function replayCommand(options: IReplayOptions): Promise<TCommandResult> {
  try {
    Logger.title('기록된 상태 재생 시작');

    // 옵션 검증
    if (!ValidationUtils.validateFilePath(options.input)) {
      return { success: false, message: '올바르지 않은 입력 파일 경로입니다.' };
    }

    if (!ValidationUtils.validateSpeed(options.speed)) {
      return { success: false, message: '올바르지 않은 재생 속도입니다.' };
    }

    const inputFile = options.input;
    const speed = parseFloat(options.speed);

    // 파일 존재 확인
    if (!(await FileUtils.fileExists(inputFile))) {
      Logger.error(`파일을 찾을 수 없습니다: ${inputFile}`);
      return { success: false, message: '입력 파일을 찾을 수 없습니다.' };
    }

    Logger.info(`재생 설정:`);
    Logger.info(`  입력 파일: ${inputFile}`);
    Logger.info(`  재생 속도: ${speed}x`);
    Logger.newLine();

    Logger.startSpinner('기록 파일을 로드하는 중...');

    // 기록 파일 로드
    const recordingData = await FileUtils.readJsonFile(inputFile);

    // 데이터 검증
    if (!validateRecordingData(recordingData)) {
      Logger.failSpinner('올바르지 않은 기록 파일 형식입니다.');
      return { success: false, message: '올바르지 않은 기록 파일 형식입니다.' };
    }

    Logger.succeedSpinner('기록 파일을 성공적으로 로드했습니다.');

    Logger.info(`기록 정보:`);
    Logger.info(`  총 스냅샷 수: ${recordingData.snapshots.length}`);
    Logger.info(`  기록 시간: ${recordingData.duration}초`);
    Logger.info(`  기록 날짜: ${new Date(recordingData.startTime).toLocaleString()}`);
    Logger.newLine();

    Logger.startSpinner('재생 시작...');

    // 재생 시작
    const replayResult = await startReplay(recordingData, speed);

    Logger.succeedSpinner('재생이 완료되었습니다!');
    
    Logger.newLine();
    Logger.success(`총 ${replayResult.processedSnapshots}개의 스냅샷을 재생했습니다.`);
    Logger.info(`재생 시간: ${replayResult.actualDuration}초`);
    Logger.newLine();

    return { 
      success: true, 
      message: '재생이 완료되었습니다.',
      data: replayResult
    };

  } catch (error) {
    Logger.failSpinner('재생 중 오류가 발생했습니다.');
    Logger.error(error instanceof Error ? error.message : '알 수 없는 오류');
    return { success: false, message: '재생에 실패했습니다.' };
  }
}

/**
 * 기록 데이터의 타입 정의
 */
interface IRecordingData {
  snapshots: Array<{
    id: string;
    timestamp: number;
    state: any;
    actionType?: string;
  }>;
  startTime: number;
  endTime: number;
  duration: number;
}

/**
 * 기록 데이터 유효성 검증 및 타입 가드
 */
function validateRecordingData(data: unknown): data is IRecordingData {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const record = data as any;

  if (!record.snapshots || !Array.isArray(record.snapshots)) {
    return false;
  }

  if (typeof record.startTime !== 'number' || 
      typeof record.endTime !== 'number' || 
      typeof record.duration !== 'number') {
    return false;
  }

  // 스냅샷 구조 검증
  for (const snapshot of record.snapshots) {
    if (!snapshot.id || typeof snapshot.timestamp !== 'number' || !snapshot.state) {
      return false;
    }
  }

  return true;
}

/**
 * 재생 시작
 */
async function startReplay(recordingData: IRecordingData, speed: number): Promise<any> {
  const startTime = Date.now();
  const snapshots = recordingData.snapshots;
  let processedSnapshots = 0;

  return new Promise((resolve) => {
    if (snapshots.length === 0) {
      resolve({
        processedSnapshots: 0,
        actualDuration: 0,
      });
      return;
    }

    let currentIndex = 0;
    const baseTimestamp = snapshots[0].timestamp;

    const processNextSnapshot = (): void => {
      if (currentIndex >= snapshots.length) {
        const endTime = Date.now();
        resolve({
          processedSnapshots,
          actualDuration: Math.floor((endTime - startTime) / 1000),
        });
        return;
      }

      const snapshot = snapshots[currentIndex];
      
      // 스냅샷 처리 시뮬레이션
      Logger.debug(`스냅샷 재생: ${snapshot.id} - ${snapshot.actionType}`);
      
      processedSnapshots++;
      currentIndex++;

      // 다음 스냅샷까지의 지연 시간 계산
      if (currentIndex < snapshots.length) {
        const nextSnapshot = snapshots[currentIndex];
        const originalDelay = nextSnapshot.timestamp - snapshot.timestamp;
        const adjustedDelay = originalDelay / speed;
        
        setTimeout(processNextSnapshot, adjustedDelay);
      } else {
        // 마지막 스냅샷
        processNextSnapshot();
      }
    };

    // 첫 번째 스냅샷부터 시작
    processNextSnapshot();
  });
}

/**
 * 재생 중단 핸들러
 */
export function handleReplayStop(): void {
  Logger.newLine();
  Logger.info('재생을 중단하는 중...');
  
  // TODO: 실제 재생 중단 로직 구현
  
  Logger.warning('재생이 중단되었습니다.');
  process.exit(0);
}

// 프로세스 종료 신호 처리
process.on('SIGINT', handleReplayStop);
process.on('SIGTERM', handleReplayStop);
